// InquiryPopup — auto-opens twice per session on every public page.
// Design mirrors "Find Your Best MBBS University" lead-gen modal from the reference site.
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { X, ShieldCheck, Star, ArrowRight, Loader2, CheckCircle2 } from 'lucide-react';
import { publicUniversityLogos } from '../lib/uniLogosApi';
import { submitInquiry } from '../lib/api';

const BASE = process.env.REACT_APP_BACKEND_URL;
const STORAGE = 'russian_edu_popup_state';
const COOLDOWN_MS = 24 * 60 * 60 * 1000;   // don't reshow within 24h after submit
const SESSION_MAX = 2;                      // maximum opens per page
const FIRST_DELAY_MS = 8000;                // first popup after 8s of landing on a page
const SECOND_DELAY_MS = 25000;              // second popup after 25s (or scroll 60%)
const SCROLL_THRESHOLD = 0.6;

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
  'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
  'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Chandigarh', 'Puducherry',
];

const NEET_OPTIONS = [
  'NEET Qualified 2025',
  'NEET Qualified 2024',
  'NEET Appearing 2026',
  'Not appeared yet',
  'Failed / Below cut-off',
];

function readState() {
  try { return JSON.parse(localStorage.getItem(STORAGE) || '{}'); } catch (_) { return {}; }
}
function writeState(patch) {
  try { localStorage.setItem(STORAGE, JSON.stringify({ ...readState(), ...patch })); } catch (_) { /* ignore */ }
}

export default function InquiryPopup() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [logos, setLogos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', email: '', state: '', neet: '' });
  const [pageOpenCount, setPageOpenCount] = useState(0);   // resets on route change

  // Load university logos strip once
  useEffect(() => {
    let off = false;
    publicUniversityLogos()
      .then((m) => {
        if (off) return;
        const urls = Object.values(m || {}).slice(0, 10).map((u) => `${BASE}${u}`);
        setLogos(urls);
      })
      .catch(() => {});
    return () => { off = true; };
  }, []);

  const openPopup = useCallback(() => {
    const st = readState();
    // Never show again for 24h after successful submit
    if (st.submittedAt && (Date.now() - st.submittedAt) < COOLDOWN_MS) return;
    setPageOpenCount((c) => c + 1);
    setOpen(true);
  }, []);

  // Global event so any CTA button on the site can open the popup on demand
  useEffect(() => {
    const forceOpen = () => setOpen(true);
    window.addEventListener('open-inquiry-popup', forceOpen);
    return () => window.removeEventListener('open-inquiry-popup', forceOpen);
  }, []);

  // Reset per-page counter on every route navigation and schedule two openings
  useEffect(() => {
    // Don't show on admin routes
    if (location.pathname.startsWith('/admin')) return;

    const st = readState();
    if (st.submittedAt && (Date.now() - st.submittedAt) < COOLDOWN_MS) return;

    // Fresh page-view — reset counter
    setPageOpenCount(0);
    let currentCount = 0;
    let scrollFired = false;

    const timer1 = setTimeout(() => {
      currentCount += 1;
      openPopup();
    }, FIRST_DELAY_MS);

    const timer2 = setTimeout(() => {
      if (currentCount >= SESSION_MAX) return;
      currentCount += 1;
      openPopup();
    }, SECOND_DELAY_MS);

    // Scroll trigger for the second nudge (whichever comes first)
    const onScroll = () => {
      if (scrollFired || currentCount >= SESSION_MAX || currentCount === 0) return;
      const el = document.documentElement;
      const pct = (el.scrollTop + window.innerHeight) / el.scrollHeight;
      if (pct >= SCROLL_THRESHOLD) {
        scrollFired = true;
        clearTimeout(timer2);
        currentCount += 1;
        openPopup();
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      window.removeEventListener('scroll', onScroll);
    };
  }, [location.pathname, openPopup]);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const close = () => setOpen(false);
  const setF = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.phone.trim()) { setError('Name and phone are required'); return; }
    if (!/^[0-9+\-\s]{7,20}$/.test(form.phone.trim())) { setError('Enter a valid phone number'); return; }
    setSubmitting(true);
    try {
      await submitInquiry({
        type: 'contact',
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        state: form.state || undefined,
        neet: form.neet || undefined,
        variant: 'popup',
        source: location.pathname,
        message: `Popup lead · State: ${form.state || '-'} · NEET: ${form.neet || '-'}`,
      });
      setSubmitted(true);
      writeState({ submittedAt: Date.now() });
      setTimeout(close, 2200);
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-up"
      onClick={close}
      data-testid="inquiry-popup-overlay"
    >
      <div
        className="relative w-full max-w-[720px] bg-white rounded-2xl shadow-2xl overflow-hidden grid md:grid-cols-[1fr_220px]"
        onClick={(e) => e.stopPropagation()}
        role="dialog" aria-modal="true"
        data-testid="inquiry-popup"
      >
        {/* Close */}
        <button
          onClick={close}
          data-testid="inquiry-popup-close"
          aria-label="Close"
          className="absolute top-3 right-3 z-20 h-8 w-8 rounded-full bg-white/95 shadow flex items-center justify-center hover:bg-white text-primary"
        >
          <X className="w-4 h-4" />
        </button>

        {submitted ? (
          <div className="md:col-span-2 p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <h3 className="font-serif text-2xl font-bold text-primary mb-2">Thank you!</h3>
            <p className="text-sm text-muted-foreground">Our counsellor will call you shortly. Sit tight — your MBBS journey to Russia is about to begin.</p>
          </div>
        ) : (
        <>
        {/* LEFT: form */}
        <div className="min-w-0 overflow-hidden">
          {/* Logos strip — infinite marquee */}
          <div className="bg-slate-50 border-b border-border px-4 pt-3 pb-2 overflow-hidden">
            <div className="popup-logos-track flex items-center gap-2">
              {(() => {
                const source = logos.length ? logos : Array.from({ length: 8 }).map(() => null);
                const doubled = [...source, ...source];
                return doubled.map((u, i) => (
                  <div key={i} className="w-8 h-8 rounded-md bg-white border border-border flex items-center justify-center shrink-0 overflow-hidden">
                    {u ? <img src={u} alt="Uni" className="max-w-6 max-h-6 object-contain" /> : null}
                  </div>
                ));
              })()}
            </div>
            <div className="mt-1.5 flex items-center gap-3 text-[10px] font-bold text-emerald-600">
              <div className="flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-emerald-500" /> NMC Recognised</div>
              <div className="flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-emerald-500" /> 100% Visa Success</div>
            </div>
          </div>

          <form onSubmit={submit} className="p-4 sm:p-5">
            <h3 className="font-serif text-lg sm:text-xl font-bold text-primary text-center leading-tight">Find Your Best MBBS University</h3>
            <p className="text-center text-[11px] sm:text-xs text-muted-foreground mt-0.5">Compare &amp; Select from 66+ Top Russian Universities</p>

            <div className="mt-3 space-y-2">
              <input
                value={form.name} onChange={setF('name')}
                placeholder="Your Name" required
                data-testid="popup-name"
                className="w-full h-10 px-4 rounded-full border border-border focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-sm"
              />
              <div>
                <input
                  value={form.phone} onChange={setF('phone')}
                  placeholder="Contact No." required inputMode="tel"
                  data-testid="popup-phone"
                  className="w-full h-10 px-4 rounded-full border border-border focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-sm"
                />
                <div className="mt-0.5 pl-3 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600">
                  <ShieldCheck className="w-2.5 h-2.5" /> We don&apos;t spam
                </div>
              </div>
              <input
                value={form.email} onChange={setF('email')}
                placeholder="Your Email" type="email"
                data-testid="popup-email"
                className="w-full h-10 px-4 rounded-full border border-border focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-sm"
              />
              <select
                value={form.state} onChange={setF('state')}
                data-testid="popup-state"
                className="w-full h-10 px-4 rounded-full border border-border bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-sm text-primary"
              >
                <option value="">Select State</option>
                {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select
                value={form.neet} onChange={setF('neet')}
                data-testid="popup-neet"
                className="w-full h-10 px-4 rounded-full border border-border bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-sm text-primary"
              >
                <option value="">NEET Status</option>
                {NEET_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            {error && (
              <div className="mt-2 text-[11px] text-brand font-semibold text-center" data-testid="popup-error">{error}</div>
            )}

            <div className="mt-2.5 flex items-center justify-between text-[10px]">
              <div className="inline-flex items-center gap-1 text-emerald-600 font-bold">
                <ShieldCheck className="w-2.5 h-2.5" /> Info is secure
              </div>
              <div className="inline-flex items-center gap-0.5 text-primary font-bold">
                {[0,1,2,3,4].map((i) => <Star key={i} className="w-2.5 h-2.5 text-amber-400" fill="#f59e0b" strokeWidth={0} />)}
                <span className="ml-1">Top Experts</span>
              </div>
            </div>

            <button
              type="submit" disabled={submitting}
              data-testid="popup-submit"
              className="mt-3 w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-white font-bold text-sm tracking-wide flex items-center justify-center gap-2 shadow-lg shadow-primary/30 disabled:opacity-60"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>FIND BEST UNIVERSITY <ArrowRight className="w-5 h-5" /></>}
            </button>
          </form>
        </div>

        {/* RIGHT: brand panel */}
        <aside
          className="hidden md:flex flex-col items-center justify-center text-center p-5 text-white relative overflow-hidden"
          style={{ background: 'linear-gradient(160deg, #1E3A8A 0%, #1E40AF 55%, #1B3A9F 100%)' }}
          data-testid="popup-brand-panel"
        >
          {/* subtle pattern */}
          <div className="absolute inset-0 opacity-15 pointer-events-none"
            style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cpath fill='%23fff' fill-opacity='0.35' d='M100 20c-8 10-4 22 6 26 8 4 20-2 22-12 2-9-4-18-14-18-4 0-9 1-14 4zm0 40c-15 10-30 30-30 55 0 20 15 40 30 40s30-20 30-40c0-25-15-45-30-55z'/%3E%3C/svg%3E\")", backgroundSize: '120px' }} />

          <div className="relative w-20 h-20 rounded-2xl bg-white shadow-xl border-2 border-white/40 flex items-center justify-center mb-4 overflow-hidden">
            <img src="/logo.jpg" alt="Russian Education" className="w-full h-full object-cover" />
          </div>

          <div className="relative">
            <div className="font-serif text-xl font-extrabold leading-tight tracking-tight">Russian</div>
            <div className="font-serif text-xl font-extrabold leading-tight tracking-tight" style={{ color: '#FCA5A5' }}>Education</div>
            <div className="mt-1 text-[9px] uppercase tracking-[0.28em] text-white/70 font-semibold">MBBS in Russia</div>
          </div>

          <div className="relative w-10 h-[2px] my-4 rounded-full" style={{ background: 'linear-gradient(90deg, transparent, #F5C86E, transparent)' }} />

          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/70">India&apos;s</div>
            <div className="mt-1 leading-none">
              <span
                className="font-serif italic text-[52px] font-black"
                style={{
                  color: 'transparent',
                  backgroundImage: 'linear-gradient(180deg, #FDE68A 0%, #F5C86E 45%, #B8860B 100%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  textShadow: '0 2px 8px rgba(184,134,11,0.35)',
                }}
              >
                No 1
              </span>
            </div>
            <div className="mt-1 font-serif text-sm font-bold leading-tight text-white">
              Education<br/>Consultancy
            </div>
          </div>

          <div className="relative mt-5 pt-4 border-t border-white/20 w-full">
            <div className="flex items-center justify-center gap-1 text-[10px] font-bold text-emerald-300">
              <ShieldCheck className="w-3 h-3" /> Trusted since 2013
            </div>
            <div className="mt-1 text-[10px] text-white/70">1500+ Students · 66 Universities</div>
          </div>
        </aside>
        </>
        )}
      </div>
    </div>
  );
}
